import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import { Check, Share } from 'lucide-react';

import { isIOS } from '../../../utils/device';
import { downloadBlobAsFile } from '../../../utils/file';
import { ShareResult, canShareFiles, shareImage } from '../platform/shareImage';
import texts from '../texts.json';
import Confetti from './Confetti';
import {
  Actions,
  Body,
  CheckBadge,
  Intro,
  PoppedSticker,
  PrimaryButton,
  Proof,
  SaveView,
  SecondaryButton,
  StatusText,
  StickerStage,
  TaskTag,
  TextButton,
  Title,
  Toast,
} from './styles';

const StoryOutcome = {
  SHARED: 'shared',
  SAVED: 'saved',
  SKIPPED: 'skipped',
};

const ShareMethod = {
  SHARE_MENU: 'shareMenu',
  SAVE_IMAGE: 'saveImage',
};

const STICKER_WIDTH = 210;

const DoneStep = ({
  stickerUrl,
  storyFile,
  storyUrl,
  onShareStarted,
  onFinish,
  onSkip,
}) => {
  const [hasSaved, setHasSaved] = useState(false);
  const [hasShareFailed, setHasShareFailed] = useState(false);
  const [isSaveViewOpen, setIsSaveViewOpen] = useState(false);
  const [toastKey, setToastKey] = useState(0);

  const canShare = canShareFiles(storyFile);
  const SaveButton = canShare ? SecondaryButton : PrimaryButton;

  // Runs straight from the tap, as the share menu requires.
  const share = () => {
    onShareStarted(ShareMethod.SHARE_MENU);
    shareImage(storyFile)
      .then((result) => {
        if (result === ShareResult.SHARED) {
          onFinish(StoryOutcome.SHARED);
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        setHasShareFailed(true);
      });
  };

  const save = () => {
    onShareStarted(ShareMethod.SAVE_IMAGE);
    // On an iPhone a download lands in Files; holding the image is how it reaches Photos.
    if (isIOS) {
      setIsSaveViewOpen(true);
    } else {
      downloadBlobAsFile(storyFile, storyFile.name);
      setToastKey(Date.now());
    }
    setHasSaved(true);
  };

  return (
    <>
      <Confetti />
      <Intro>
        <TaskTag $hasBadge>
          <CheckBadge $size={20}><Check size={12} strokeWidth={3.4} aria-hidden="true" /></CheckBadge>
          {texts.done.tag}
        </TaskTag>
        <Title $size={36}>{texts.done.title}</Title>
        <Body>{texts.done.body}</Body>
      </Intro>
      <StickerStage>
        <PoppedSticker src={stickerUrl} alt={texts.done.sticker} $width={STICKER_WIDTH} />
      </StickerStage>
      <Actions>
        <Proof>
          {texts.done.proofLead}
          <br />
          <strong>{texts.done.proof}</strong>
        </Proof>
        {canShare && (
          <PrimaryButton type="button" onClick={share}>
            <Share size={18} strokeWidth={2.4} aria-hidden="true" />
            {texts.done.share}
          </PrimaryButton>
        )}
        <SaveButton type="button" onClick={save}>{texts.done.save}</SaveButton>
        {hasShareFailed && <StatusText role="alert">{texts.done.shareFailed}</StatusText>}
        {hasSaved
          ? <TextButton type="button" onClick={() => onFinish(StoryOutcome.SAVED)}>{texts.done.continue}</TextButton>
          : <TextButton type="button" onClick={onSkip}>{texts.done.skip}</TextButton>}
      </Actions>
      {!!toastKey && <Toast key={toastKey} role="status">{texts.done.saved}</Toast>}
      {isSaveViewOpen && (
        <SaveView role="dialog" aria-label={texts.saveView.title}>
          <img src={storyUrl} alt={texts.done.sticker} />
          <p>{texts.saveView.title}</p>
          <SecondaryButton type="button" onClick={() => setIsSaveViewOpen(false)}>
            {texts.saveView.close}
          </SecondaryButton>
        </SaveView>
      )}
    </>
  );
};

DoneStep.propTypes = {
  stickerUrl: PropTypes.string.isRequired,
  storyFile: PropTypes.instanceOf(File).isRequired,
  storyUrl: PropTypes.string.isRequired,
  onShareStarted: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

export { StoryOutcome };
export default DoneStep;
